import { endOfDay, startOfDay, subDays } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import DateRangePicker, { RangeType } from 'rsuite/DateRangePicker';
import 'rsuite/DateRangePicker/styles/index.css';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Action } from 'src/interfaces/action.interface';
import { Agent } from 'src/interfaces/agent.interface';
import { CallFilters } from '../../interfaces/call.interface';
import { statusesOptions } from '../../stubs/calls.stub';
import { getFirstAndLastFourUUID } from '../../utils/number';
import {
  AutoCompleteMultiSelectBox,
  Option,
} from '../AutoCompleteMultiSelectBox';
import { AutoCompleteSelectBox } from '../AutoCompleteSelectBox';
import { Button } from '../Button';
import { Input } from '../Input';
import { Label } from '../Label';
import { Modal } from '../Modal';
import { SelectBox } from '../SelectBox';

import './style.css';

interface CallFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: CallFilters;
  onApplyFilters: (filters: CallFilters) => void;
  agents: Agent[];
  searchAgents: (name: string) => Promise<{ value: string; label: string }[]>;
  loading: boolean;
}

const durationComparisonOptions = [
  { value: 'gt', label: '>' },
  { value: 'gte', label: '>=' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '<=' },
  { value: 'eq', label: '=' },
];

export const CallFiltersModal = ({
  isOpen,
  onClose,
  filters,
  onApplyFilters,
  agents,
  searchAgents,
  loading,
}: CallFiltersModalProps) => {
  const [localFilters, setLocalFilters] = useState<CallFilters>(filters);
  const [dateRange, setDateRange] = useState<[Date, Date]>(() => {
    const end = endOfDay(new Date());
    const start = startOfDay(subDays(end, 27));
    return [start, end];
  });
  const [actions, setActions] = useState<Action[]>([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const { get } = useAuthedRequest();

  const agentsOptions = [
    { value: 'All Agents' },
    ...agents.map((agent: Agent) => ({
      value: agent.id as string,
      label: agent.name || getFirstAndLastFourUUID(agent.id as string),
    })),
  ];

  const customRanges = [
    {
      label: 'Last 28 days',
      value: [startOfDay(subDays(new Date(), 27)), endOfDay(new Date())],
      closeOverlay: true,
    },
    {
      label: 'Last 7 days',
      value: [startOfDay(subDays(new Date(), 6)), endOfDay(new Date())],
      closeOverlay: true,
    },
    {
      label: 'Yesterday',
      value: [
        startOfDay(subDays(new Date(), 1)),
        endOfDay(subDays(new Date(), 1)),
      ],
      closeOverlay: true,
    },
    {
      label: 'Today',
      value: [startOfDay(new Date()), endOfDay(new Date())],
      closeOverlay: true,
    },
  ];

  const fetchActions = useCallback(
    async (query?: string) => {
      setActionsLoading(true);
      try {
        const queryParams = new URLSearchParams({
          size: '50',
          filters: JSON.stringify({
            scope: 'agent',
            ...(query && query.length > 0 && { label: query }),
          }),
        });

        const { items } = await get(`/actions?${queryParams.toString()}`);
        setActions(items);
      } catch (error) {
        console.error('Error fetching actions:', error);
      } finally {
        setActionsLoading(false);
      }
    },
    [get],
  );

  const actionOptions: Option[] = actions.map((action) => ({
    value: JSON.stringify({ type: action.type, label: action.label }),
    label: action.label || action.type,
  }));

  const selectedActionOptions: Option[] = (
    localFilters.successful_actions || []
  ).map((action) => ({
    value: JSON.stringify({ type: action.type, label: action.label }),
    label: action.label || action.type,
  }));

  const handleSuccessfulActionsChange = (options: Option[]) => {
    const successfulActions = options.map((option) => {
      const parsed = JSON.parse(option.value);
      return { type: parsed.type, label: parsed.label };
    });
    updateLocalFilters({ successful_actions: successfulActions });
  };

  const handleApply = () => {
    const updatedFilters: CallFilters = {
      ...localFilters,
      ...(dateRange &&
      dateRange[0] instanceof Date &&
      dateRange[1] instanceof Date
        ? {
            start_time: {
              comparison: 'btwn',
              value: [dateRange[0].toISOString(), dateRange[1].toISOString()],
            },
          }
        : { start_time: null }),
    };
    onApplyFilters(updatedFilters);
    onClose();
  };

  const handleReset = () => {
    const end = endOfDay(new Date());
    const start = startOfDay(subDays(end, 27));
    const resetFilters: CallFilters = {
      agent: 'All Agents',
      status: 'All Statuses',
      call_id: '',
      from_number: '',
      to_number: '',
      start_time: {
        comparison: 'btwn',
        value: [start.toISOString(), end.toISOString()],
      },
      duration: undefined,
      successful_actions: [],
      transcript_keyword: '',
    };
    setLocalFilters(resetFilters);
    setDateRange([start, end]);
  };

  const updateLocalFilters = (newFilters: Partial<CallFilters>) => {
    setLocalFilters((prev: CallFilters) => ({ ...prev, ...newFilters }));
  };

  useEffect(() => {
    if (isOpen && actions.length === 0) {
      fetchActions();
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Filter Calls"
      className="lg:max-w-[800px]"
      actionButton={
        <div className="flex gap-2">
          <Button onClick={handleReset} color="default">
            Reset
          </Button>
          <Button onClick={handleApply} disabled={loading}>
            Apply Filters
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Date Range */}
        <div>
          <Label htmlFor="period">Period</Label>
          <DateRangePicker
            format="MM/dd/yyyy hh:mm aa"
            showMeridiem
            className="w-full"
            value={dateRange}
            onChange={(value) => setDateRange(value as [Date, Date])}
            ranges={customRanges as RangeType[]}
          />
        </div>

        {/* Agent and Status Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="agents">Agents</Label>
            <AutoCompleteSelectBox
              key={`agent-${localFilters.agent}`}
              defaultValue={agentsOptions.find(
                (agent) => agent.value === localFilters.agent,
              )}
              fetchOptions={searchAgents}
              onChange={(value) => updateLocalFilters({ agent: value })}
              variant="outlined"
              color="primary"
              size="medium"
              disabled={loading}
              defaultOptions={agentsOptions}
              allowRemoveOption={false}
            />
          </div>

          <div>
            <Label htmlFor="status">Statuses</Label>
            <SelectBox
              options={statusesOptions}
              value={statusesOptions.find(
                (status) => status.value === localFilters.status,
              )}
              variant="outlined"
              color="primary"
              size="medium"
              onChange={(value) => updateLocalFilters({ status: value })}
            />
          </div>
        </div>

        {/* Call ID and Phone Numbers Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Input
              label="Call ID"
              type="text"
              value={localFilters.call_id || ''}
              onChange={(e) => updateLocalFilters({ call_id: e.target.value })}
            />
          </div>

          <div>
            <Input
              label="From Number"
              type="text"
              value={localFilters.from_number}
              onChange={(e) =>
                updateLocalFilters({ from_number: e.target.value })
              }
            />
          </div>

          <div className="md:col-span-1">
            <Input
              label="To Number"
              type="text"
              value={localFilters.to_number}
              onChange={(e) =>
                updateLocalFilters({ to_number: e.target.value })
              }
            />
          </div>
        </div>

        {/* Duration Filter */}
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="w-full">
              <Label htmlFor="duration">Duration (seconds)</Label>

              <div className="flex">
                <SelectBox
                  options={durationComparisonOptions}
                  value={durationComparisonOptions.find(
                    (option) =>
                      option.value === localFilters.duration?.comparison,
                  )}
                  variant="outlined"
                  color="primary"
                  size="medium"
                  onChange={(value) =>
                    updateLocalFilters({
                      duration: {
                        comparison: value,
                        value: localFilters.duration?.value || 0,
                      },
                    })
                  }
                  className="w-full rounded-r-none"
                />

                <Input
                  type="number"
                  placeholder="0"
                  value={localFilters.duration?.value || ''}
                  onChange={(e) =>
                    updateLocalFilters({
                      duration: {
                        comparison: localFilters.duration?.comparison || 'gt',
                        value: parseInt(e.target.value) || 0,
                      },
                    })
                  }
                  variant="outlined"
                  color="primary"
                  size="medium"
                  className="w-full rounded-l-none ml-[-1px] py-[0.53rem]"
                  fullWidth={true}
                />
              </div>
            </div>

            {/* Successful Actions Filter */}
            <div className="w-full">
              <Label htmlFor="successful-actions">Successful Actions</Label>
              <AutoCompleteMultiSelectBox
                fetchOptions={fetchActions}
                selectedOptions={selectedActionOptions}
                options={actionOptions}
                onSelectionChange={handleSuccessfulActionsChange}
                variant="outlined"
                color="primary"
                size="medium"
                placeholder="Select completed actions..."
                noOptionsText="No actions found"
                disabled={actionsLoading}
              />
            </div>
          </div>
        </div>

        {/* Transcript Keyword */}
        <div>
          <Input
            label="Transcript Keyword"
            type="text"
            placeholder="Search in transcript..."
            value={localFilters.transcript_keyword}
            onChange={(e) =>
              updateLocalFilters({ transcript_keyword: e.target.value })
            }
          />
        </div>
      </div>
    </Modal>
  );
};
